import React from 'react';
import { useQuery } from 'react-query';
import { ChartData, dataExistsChecker, handleSales } from '../utils/reusable-func';
import api from '../../../../components/api';

function useRenderChartTotal() {
  /* Handle current view of chart
  * 0. Today
  * 1. This Week
  * 2. This Month
  * 3. 3 Months
  * 4. 6 Months
  * 5. 1 Year
  */
  const [currentView, setCurrentView] = React.useState<number>(0);

  /*
  * Get Chart Data
  * Need to be run with enabled after finish fetching first data for handling error from Back End
  */
  // 1. Today Data
  const { data: today, isLoading: todayLoad } = useQuery(
    ['hourly', 'total', 'analytics', 1],
    () => api.analytics.total.hourly(1),
  );
  // 2. Week Data
  const { data: week, isLoading: weekLoad } = useQuery(
    ['hourly', 'total', 'analytics', 7],
    () => api.analytics.total.hourly(7),
    {
      enabled: !!today,
    },
  );
  // 3. Month Data
  const { data: month, isLoading: monthLoad } = useQuery(
    ['daily', 'total', 'analytics', 30],
    () => api.analytics.total.daily(30),
    {
      enabled: !!week,
    },
  );
  // 4. Three Month Data
  const { data: threeMonth, isLoading: threeMonthLoad } = useQuery(
    ['daily', 'total', 'analytics', 90],
    () => api.analytics.total.daily(90),
    {
      enabled: !!month,
    },
  );
  // 5. Six Month Data
  const { data: sixMonth, isLoading: sixMonthLoad } = useQuery(
    ['weekly', 'total', 'analytics', 6],
    () => api.analytics.total.weekly(6),
    {
      enabled: !!threeMonth,
    },
  );
  // 6. Year Data
  const { data: year, isLoading: yearLoad } = useQuery(
    ['monthly', 'total', 'analytics', 12],
    () => api.analytics.total.monthly(12),
    {
      enabled: !!sixMonth,
    },
  );

  // Data Management
  const totalSales: ChartData | undefined = React.useMemo(() => handleSales({
    today: today?.data?.data,
    oneWeek: week?.data?.data,
    oneMonth: month?.data?.data,
    threeMonth: threeMonth?.data?.data,
    sixMonth: sixMonth?.data?.data,
    year: year?.data?.data,
  }, currentView), [year, currentView]);

  // Checking data exists or not for rendering chart
  const isDataExists: boolean = React.useMemo(() => dataExistsChecker({
    today: today?.data?.data,
    oneWeek: week?.data?.data,
    oneMonth: month?.data?.data,
    threeMonth: threeMonth?.data?.data,
    sixMonth: sixMonth?.data?.data,
    year: year?.data?.data,
  }), [year]);

  // Checking API Loading status
  const isLoading: boolean = React.useMemo(
    () => todayLoad || weekLoad || monthLoad || threeMonthLoad || sixMonthLoad || yearLoad,
    [todayLoad, weekLoad, monthLoad, threeMonthLoad, sixMonthLoad, yearLoad],
  );

  return {
    currentView,
    setCurrentView,
    totalSales,
    isLoading,
    isDataExists,
  };
}

export default useRenderChartTotal;
